import { type RefObject, useEffect } from "react";

const LOCKED_FIELD_ATTR = "data-prevent-initial-focus";
const PREV_TABINDEX_ATTR = "data-prev-tabindex";

function isFormField(el: EventTarget | null): el is HTMLElement {
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
  );
}

function isLockableTextField(
  field: HTMLInputElement | HTMLTextAreaElement,
): boolean {
  const type = field.type;
  return (
    type !== "hidden" &&
    type !== "submit" &&
    type !== "button" &&
    type !== "checkbox" &&
    type !== "radio" &&
    type !== "file"
  );
}

function getFormFields(root: HTMLElement) {
  return root.querySelectorAll<HTMLElement>("input, textarea, select");
}

function lockFormFields(root: HTMLElement) {
  getFormFields(root).forEach((field) => {
    if (field.getAttribute(LOCKED_FIELD_ATTR) === "true") return;

    field.setAttribute(LOCKED_FIELD_ATTR, "true");

    if (
      field instanceof HTMLInputElement ||
      field instanceof HTMLTextAreaElement
    ) {
      if (isLockableTextField(field)) {
        field.readOnly = true;
      }
      return;
    }

    if (field instanceof HTMLSelectElement) {
      if (!field.hasAttribute(PREV_TABINDEX_ATTR)) {
        field.setAttribute(PREV_TABINDEX_ATTR, String(field.tabIndex));
      }
      field.tabIndex = -1;
    }
  });
}

function unlockFormFields(root: HTMLElement) {
  getFormFields(root).forEach((field) => {
    if (field.getAttribute(LOCKED_FIELD_ATTR) !== "true") return;

    field.removeAttribute(LOCKED_FIELD_ATTR);

    if (
      field instanceof HTMLInputElement ||
      field instanceof HTMLTextAreaElement
    ) {
      field.readOnly = false;
      return;
    }

    if (field instanceof HTMLSelectElement) {
      const prevTabIndex = field.getAttribute(PREV_TABINDEX_ATTR);
      field.tabIndex = prevTabIndex ? Number(prevTabIndex) : 0;
      field.removeAttribute(PREV_TABINDEX_ATTR);
    }
  });
}

function resetWindowScroll() {
  if (window.scrollX === 0 && window.scrollY === 0) return;
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
}

/**
 * Prevents inputs inside `containerRef` from receiving focus on initial page load
 * (browser/password-manager autofocus). Fields are read-only until the user taps
 * or presses Tab so mobile browsers do not open the keyboard on load. Resets
 * scroll only when an autofocus blur cycle would offset the page.
 */
export function usePreventInitialInputFocus(
  containerRef: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    let allowFieldFocus = false;

    const enableFieldFocus = () => {
      if (allowFieldFocus) return;
      allowFieldFocus = true;
      unlockFormFields(root);
      observer.disconnect();
    };

    const blurFocusedField = () => {
      const active = document.activeElement;
      if (!active || !isFormField(active) || !root.contains(active)) {
        return;
      }
      active.blur();
      if (!allowFieldFocus) {
        resetWindowScroll();
      }
    };

    const onFocusIn = (event: FocusEvent) => {
      if (allowFieldFocus) return;

      const target = event.target;
      if (!isFormField(target) || !root.contains(target)) {
        return;
      }

      target.blur();
      resetWindowScroll();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        enableFieldFocus();
      }
    };

    lockFormFields(root);

    const observer = new MutationObserver(() => {
      if (!allowFieldFocus) {
        lockFormFields(root);
      }
    });
    observer.observe(root, { childList: true, subtree: true });

    document.addEventListener("pointerdown", enableFieldFocus, true);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("focusin", onFocusIn, true);

    blurFocusedField();
    const frame = requestAnimationFrame(blurFocusedField);
    const timer = window.setTimeout(blurFocusedField, 0);
    const scrollTimer = window.setTimeout(resetWindowScroll, 50);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      window.clearTimeout(scrollTimer);
      observer.disconnect();
      unlockFormFields(root);
      document.removeEventListener("pointerdown", enableFieldFocus, true);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("focusin", onFocusIn, true);
    };
  }, [containerRef]);
}
